import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Anchor,
  Avatar,
  Button,
  Checkbox,
  Divider,
  NumberFormatter,
  Select
} from '@mantine/core';
import { File } from 'tabler-icons-react';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import { ECOM_EXTERNAL_CONNECTION_TYPE_ENUM } from '../../../config/constants';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import { EXTERNAL_CONNECTION_CONFIGS } from '../external/config/externalConnectionConfigs';
import { parsePapaCsv } from '../../../helpers/csvHelper';
import FileUpload from '../../common/FileUpload';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { singularPluralFormat } from '../../../helpers/format';
import PaginationList from '../../common/PaginationList';
import AppCheckbox from '../../common/AppCheckbox';

const AdminProductImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [formState, setFormState] = useState({
    fkEcomExternalConnectionType: ECOM_EXTERNAL_CONNECTION_TYPE_ENUM.DUPLIUM,
    ecomExternalProducts: [],
    file: null,
    fileLoading: false,
    importData: null,
    hideExistingProducts: false,
    showProductLinkView: false,
    syncAfterLink: false,
    syncOptions: [],
    loading: false,
    showImportDetails: false,
    showImportFields: false
  });
  const {
    importEcomExternalProducts,
    linkEcomExternalProducts,
    fetchEcomExternalProductsForImport
  } = useContext(AdminContext);
  const externalConnectionConfig = EXTERNAL_CONNECTION_CONFIGS.find(
    (c) =>
      c.pkEcomExternalConnectionType === formState.fkEcomExternalConnectionType
  );
  const syncOptions = externalConnectionConfig?.products?.syncOptions || [];

  const filteredProducts = formState.ecomExternalProducts
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((p) => !formState.hideExistingProducts || !p.pkEcomExternalProduct);

  useEffect(() => {
    if (isOpen) {
      setFormState({
        fkEcomExternalConnectionType:
          ECOM_EXTERNAL_CONNECTION_TYPE_ENUM.DUPLIUM,
        ecomExternalProducts: [],
        file: null,
        fileLoading: false,
        importData: null,
        hideExistingProducts: false,
        showProductLinkView: false,
        syncAfterLink: false,
        syncOptions: [],
        loading: false,
        showImportDetails: false,
        showImportFields: false
      });
    }
  }, [isOpen]);

  const onUploadCsv = (file) => {
    setFormState((c) => ({
      ...c,
      file: null,
      importData: null,
      fileLoading: true
    }));
    parsePapaCsv(
      file,
      externalConnectionConfig?.importConfig?.fields || [],
      async (csvData) => {
        fetchEcomExternalProductsForImport(
          formState.fkEcomExternalConnectionType,
          (existingProductData) => {
            try {
              const productData =
                externalConnectionConfig.importConfig.onParseCsv(
                  csvData,
                  existingProductData
                ) || [];
              setFormState((c) => ({
                ...c,
                fileLoading: false,
                file,
                importData: productData,
                ecomExternalProducts: productData.map((p) => ({
                  ...p,
                  ecomExternalProductVariants: p.variants.map((v) => ({
                    ...v,
                    ecomExternalProductVariantOptions: v.options || []
                  }))
                }))
              }));
            } catch (e) {
              triggerNotification('Error parsing file');
              setFormState((c) => ({
                ...c,
                fileLoading: false,
                file: null
              }));
            }
          },
          (e) => {
            triggerNotification(e);
            setFormState((c) => ({
              ...c,
              fileLoading: false,
              file: null
            }));
          }
        );
      }
    );
  };

  const onSubmitImport = () => {
    setFormState((c) => ({ ...c, loading: true }));
    importEcomExternalProducts(
      externalConnectionConfig.pkEcomExternalConnectionType,
      { ecomExternalProducts: formState.ecomExternalProducts },
      (d) => {
        triggerNotification('Products imported', 'success');
        onSuccess(d);
      },
      (e) => {
        triggerNotification(e);
        setFormState({ ...formState, loading: false });
      }
    );
  };

  const onSubmitProductsLink = () => {
    setFormState((c) => ({ ...c, loading: true }));
    linkEcomExternalProducts(
      externalConnectionConfig.pkEcomExternalConnectionType,
      {
        syncAfterLink: formState.syncAfterLink,
        syncOptions: formState.syncOptions
      },
      (d) => {
        triggerNotification('Products linked', 'success');
        onSuccess(d);
      },
      (e) => {
        triggerNotification(e);
        setFormState({ ...formState, loading: false });
      }
    );
  };

  return (
    <ResponsiveModal
      formSectionProps={
        formState.showImportFields
          ? {
              isSubmitHidden: true,
              onCancel: () => {
                setFormState({ ...formState, showImportFields: false });
              },
              cancelTitle: 'Back'
            }
          : formState.showProductLinkView
          ? {
              onCancel: () => {
                setFormState({ ...formState, showProductLinkView: false });
              },
              isLoading: formState.loading,
              onSubmit: onSubmitProductsLink,
              submitTitle: 'Link imported products'
            }
          : formState.showImportDetails
          ? {
              onCancel: onClose,
              isLoading: formState.loading,
              onSubmit: onSubmitImport,
              isSubmitDisabled: !formState.importData,
              submitTitle: `Import ${singularPluralFormat(
                formState.ecomExternalProducts?.length || 0,
                'product',
                'products'
              )}`
            }
          : {
              onCancel: onClose,
              isLoading: formState.loading,
              onSubmit: () => {
                setFormState({ ...formState, showImportDetails: true });
              },
              isSubmitDisabled: !formState.importData,
              submitTitle: 'Continue',
              leftSection: (
                <Anchor
                  onClick={() => {
                    setFormState({
                      ...formState,
                      showProductLinkView: true
                    });
                  }}
                  style={{ fontSize: 14, fontWeight: 500 }}
                >
                  Link products
                </Anchor>
              )
            }
      }
      isOpen={isOpen}
      onClose={onClose}
      title="Import external products"
    >
      <AppStack style={{ gap: 20 }}>
        {formState.showProductLinkView ? (
          <AppStack>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              This action will attempt to link all imported products with an
              external source using their model IDs.
            </AppText>

            <Select
              data={EXTERNAL_CONNECTION_CONFIGS.filter(
                (c) => !!c.importConfig
              ).map((c) => ({
                label: c.title,
                value: c.pkEcomExternalConnectionType
              }))}
              description="Select the external provider you want to link products for."
              disabled={formState.loading}
              label="External provider"
              onChange={(value) => {
                setFormState({
                  ...formState,
                  fkEcomExternalConnectionType: value
                });
              }}
              value={formState.fkEcomExternalConnectionType}
            />

            {syncOptions.length > 0 && (
              <>
                <Checkbox
                  checked={formState.syncAfterLink}
                  disabled={formState.loading}
                  label="Sync data after linking"
                  onChange={() => {
                    setFormState({
                      ...formState,
                      syncAfterLink: !formState.syncAfterLink
                    });
                  }}
                  style={{ marginTop: 8 }}
                />

                {formState.syncAfterLink && (
                  <AppStack style={{ gap: 16, marginTop: 8 }}>
                    <Divider />
                    <AppStack style={{ gap: 5 }}>
                      <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                        Sync Options:
                      </AppText>
                      <AppText
                        style={{
                          fontSize: 14,
                          color: '#666',
                          fontStyle: 'italic'
                        }}
                      >
                        Select the options you want to sync:
                      </AppText>
                    </AppStack>
                    <AppCard
                      radius="md"
                      shadow="none"
                      style={{ padding: 0 }}
                      withBorder
                    >
                      <AppStack style={{ gap: 0 }}>
                        {syncOptions.map((option, index) => {
                          const selected = formState.syncOptions.includes(
                            option.dataValue
                          );

                          return (
                            <React.Fragment key={option.dataValue}>
                              {index > 0 && <Divider />}
                              <AppFlexbox
                                onClick={(e) => {
                                  setFormState({
                                    ...formState,
                                    syncOptions: selected
                                      ? formState.syncOptions.filter(
                                          (s) => s !== option.dataValue
                                        )
                                      : [
                                          ...formState.syncOptions,
                                          option.dataValue
                                        ]
                                  });
                                }}
                                style={{ padding: 16, cursor: 'pointer' }}
                              >
                                <AppCheckbox
                                  key={option.dataValue}
                                  checked={selected}
                                  description={option.description}
                                  disabled={formState.loading}
                                  label={option.label}
                                  onChange={() => {}}
                                  style={{
                                    fontWeight: 500,
                                    pointerEvents: 'none'
                                  }}
                                />
                              </AppFlexbox>
                            </React.Fragment>
                          );
                        })}
                      </AppStack>
                    </AppCard>
                  </AppStack>
                )}
              </>
            )}
          </AppStack>
        ) : formState.showImportDetails ? (
          <AppStack>
            <AppStack style={{ gap: 5 }}>
              <AppFlexbox
                style={{
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <AppFlexbox style={{ gap: 5 }}>
                  <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                    Products
                  </AppText>

                  <Avatar color="blue" radius="xl" size="sm" variant="filled">
                    <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                      {filteredProducts.length}
                    </AppText>
                  </Avatar>
                </AppFlexbox>

                <Checkbox
                  checked={formState.hideExistingProducts}
                  label="Hide existing products"
                  onChange={() => {
                    setFormState({
                      ...formState,
                      hideExistingProducts: !formState.hideExistingProducts
                    });
                  }}
                />
              </AppFlexbox>

              <PaginationList
                emptyMessage="No products found."
                items={filteredProducts.map((p) => {
                  const hasPricing = p.ecomExternalProductVariants.some(
                    (v) => v.price
                  );
                  const pricing = hasPricing
                    ? p.ecomExternalProductVariants.map((v) => v.price || 0)
                    : [0];
                  const maxPrice = Math.max(...pricing);
                  const minPrice = Math.min(...pricing);

                  return (
                    <AppFlexbox
                      key={p.key}
                      style={{
                        gap: 8,
                        padding: 8,
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <AppFlexbox
                        style={{
                          flex: 1,
                          gap: 8,
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <AppStack style={{ gap: 0 }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 500
                            }}
                          >
                            {p.name}
                          </AppText>

                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666'
                            }}
                          >
                            {p.model}
                          </AppText>

                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666'
                            }}
                          >
                            {singularPluralFormat(
                              p.ecomExternalProductVariants.length || 0,
                              'variant',
                              'variants'
                            )}
                          </AppText>
                        </AppStack>

                        <AppFlexbox>
                          {hasPricing && (
                            <AppText style={{ fontSize: 14, color: '#666' }}>
                              {minPrice === maxPrice ? (
                                <NumberFormatter
                                  decimalScale={2}
                                  fixedDecimalScale
                                  prefix="$"
                                  thousandSeparator
                                  value={maxPrice / 100}
                                />
                              ) : (
                                <>
                                  <NumberFormatter
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix="$"
                                    thousandSeparator
                                    value={minPrice / 100}
                                  />{' '}
                                  -{' '}
                                  <NumberFormatter
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix="$"
                                    thousandSeparator
                                    value={maxPrice / 100}
                                  />
                                </>
                              )}
                            </AppText>
                          )}
                        </AppFlexbox>
                      </AppFlexbox>
                    </AppFlexbox>
                  );
                })}
                itemsPerPage={10}
                onPageChange={(pageIndex) =>
                  setFormState({
                    ...formState,
                    pageIndex
                  })
                }
                pageIndex={formState.pageIndex}
              />
            </AppStack>
          </AppStack>
        ) : formState.file ? (
          <AppStack>
            <AppCard style={{ padding: 0 }} withBorder>
              <AppFlexbox style={{ gap: 0 }}>
                <AppStack
                  style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 10
                  }}
                >
                  <File size={45} />
                </AppStack>
                <Divider orientation="vertical" />
                <AppFlexbox style={{ flex: 1, gap: 8, alignItems: 'center' }}>
                  <AppStack
                    style={{
                      flex: 1,
                      padding: 10,
                      gap: 0
                    }}
                  >
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      {formState.file.name}
                    </AppText>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      {singularPluralFormat(
                        formState.importData?.length || 0,
                        'product',
                        'products'
                      )}
                    </AppText>
                  </AppStack>

                  <AppStack
                    style={{
                      padding: 10,
                      gap: 0
                    }}
                  >
                    <Anchor
                      onClick={() => {
                        setFormState({
                          ...formState,
                          file: null,
                          importData: null
                        });
                      }}
                      style={{ fontSize: 12, color: 'dodgerblue' }}
                    >
                      Change
                    </Anchor>
                  </AppStack>
                </AppFlexbox>
              </AppFlexbox>
            </AppCard>
          </AppStack>
        ) : formState.showImportFields ? (
          <AppStack>
            <AppText
              style={{ fontSize: 14, color: '#666', textAlign: 'center' }}
            >
              Below are the fields that will be used when importing your CSV for{' '}
              <span style={{ fontWeight: 500 }}>
                {externalConnectionConfig?.title}
              </span>
              .
            </AppText>

            <AppStack style={{ gap: 5 }}>
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                {externalConnectionConfig?.title} import fields:
              </AppText>

              <AppStack style={{ gap: 0, border: 'solid 1px lightgrey' }}>
                {externalConnectionConfig?.importConfig?.fields.map(
                  (field, index) => (
                    <React.Fragment key={field.dataValue}>
                      {index > 0 && <Divider />}

                      <AppStack style={{ gap: 5, padding: 8 }}>
                        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                          {field.label}
                        </AppText>
                        {field.description && (
                          <AppText style={{ fontSize: 12, color: '#666' }}>
                            {field.description}
                          </AppText>
                        )}
                        {field.isRequired && (
                          <AppText style={{ fontSize: 12, color: '#C40000' }}>
                            * Required
                          </AppText>
                        )}
                      </AppStack>
                    </React.Fragment>
                  )
                )}
              </AppStack>
            </AppStack>
          </AppStack>
        ) : (
          <>
            <AppStack style={{ gap: 5 }}>
              <AppFlexbox
                style={{
                  gap: 10,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  External Products
                </AppText>

                <Button
                  color="blue"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      showImportFields: true
                    });
                  }}
                  size="compact-sm"
                  variant="outline"
                >
                  View fields
                </Button>
              </AppFlexbox>
              <Select
                data={EXTERNAL_CONNECTION_CONFIGS.filter(
                  (c) => !!c.importConfig
                ).map((c) => ({
                  label: c.title,
                  value: c.pkEcomExternalConnectionType
                }))}
                onChange={(value) => {
                  setFormState({
                    ...formState,
                    fkEcomExternalConnectionType: value
                  });
                }}
                value={formState.fkEcomExternalConnectionType}
              />
            </AppStack>

            <FileUpload
              file={formState.file}
              height={200}
              loading={formState.fileLoading}
              onUpload={(files) => {
                onUploadCsv(files[0]);
              }}
            />
          </>
        )}
      </AppStack>
    </ResponsiveModal>
  );
};

AdminProductImportModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
};

export default AdminProductImportModal;
